import React, { useEffect, useState } from 'react';
import { defineMessages, FormattedMessage, useIntl } from 'react-intl';
import CropImageField from '@components/common/form/CropImageField';
import DropdownField from '@components/common/form/DropdownField';
import { AppConstants,DATE_DISPLAY_FORMAT,DATE_FORMAT_DISPLAY,DEFAULT_FORMAT, DEFAULT_FORMAT_ZERO_SECOND, TIME_FORMAT_DISPLAY } from '@constants';
import apiConfig from '@constants/apiConfig';
import { commonLanguages, paypalCurrentcyCodes, restaurantServices, RestaurantStatus } from '@constants/masterData';
import useBasicForm from '@hooks/useBasicForm';
import useFetch from '@hooks/useFetch';
import useTranslate from '@hooks/useTranslate';
import { BaseForm, DatePickerField, NumericField, SelectField, TextField } from '@itz/react-cms-element';
import { commonMessage } from '@locales/intl';
import { convertUtcToLocalTime, handleRemoveImage } from '@utils';
import { Card, Col, Form, Row } from 'antd';
import dayjs from 'dayjs';

const message = defineMessages({
    objectName: 'restaurant',
    requiredExpiredDate: 'Expiration date is required',
    requiredExtendDate: 'Extend date is required',
    validateDueDate: 'The end date must be later than the start date.',
});

const RestaurantExpiredForm = (props) => {
    const intl = useIntl();
    const translate = useTranslate();
    const { formId, actions, dataDetail, onSubmit, setIsChangedFormValues, branchs, isEditing } = props;
    const { execute: executeUpFile } = useFetch(apiConfig.file.upload);
    const [ imageUrl, setImageUrl ] = useState(null);
    const [ bannerUrl, setBannerUrl ] = useState(null);
    const statusValues = translate.formatKeys(RestaurantStatus, ['label']);
    const [ expiredDateDefault, setExpiredDateDefault ] = useState();

    const { form, mixinFuncs, onValuesChange } = useBasicForm({
        onSubmit,
        setIsChangedFormValues,
    });

    const uploadFile = (file) => {
        executeUpFile({
            data: {
                type: 'LOGO',
                file: file,
            },
            onCompleted: (response) => {
                if (response.result === true) {
                    setImageUrl(response.data.filePath);
                    setIsChangedFormValues(true);
                }
            },
            onError: (error) => {},
        });
    };
    const uploadBanner = (file) => {
        executeUpFile({
            data: {
                type: 'LOGO',
                file: file,
            },
            onCompleted: (response) => {
                if (response.result === true) {
                    setBannerUrl(response.data.filePath);
                    setIsChangedFormValues(true);
                }
            },
            onError: (error) => {},
        });
    };

    const handleSubmit = (values) => {
        const formattedValues = {
            ...values,
            expiredDate: values.expiredDate ? dayjs(values.expiredDate).format(DEFAULT_FORMAT) : null,
            extendDate: values.extendDate ? dayjs(values.extendDate).format(DEFAULT_FORMAT) : null,
            logoPath: imageUrl,
            bannerPath: bannerUrl,
            settings: dataDetail.settings || '{}',
            accountId: dataDetail.accountId,
            services: Array.isArray(values.services) ? values.services.join(';') : values.services,
        };
        
        return mixinFuncs.handleSubmit(formattedValues);
    };

    useEffect(() => {
        const toDayjs = (value) => {
            if (!value) return undefined;
            if (dayjs.isDayjs(value)) return value;
            return dayjs(convertUtcToLocalTime(value, DEFAULT_FORMAT, DEFAULT_FORMAT), DEFAULT_FORMAT_ZERO_SECOND);
        };
        form.setFieldsValue({
            ...dataDetail,
            expiredDate: toDayjs(dataDetail.expiredDate),
            extendDate: toDayjs(dataDetail.extendDate),
            latitude: dataDetail.latitude,
            longitude: dataDetail.longitude,
            services: dataDetail.services
                ?.split(';')
                .filter((item, index, self) => item && self.indexOf(item) === index),
            settings: dataDetail.settings,
        });
        setBannerUrl(dataDetail.bannerPath);
        setImageUrl(dataDetail.logoPath);
    }, [ dataDetail ]);

    const validateDueDate = (_, value) => {
        const { expiredDate } = form.getFieldValue();
        if (expiredDate && value && value.isBefore(expiredDate)) {
            return Promise.reject(intl.formatMessage(message.validateDueDate));
        }
        return Promise.resolve();
    };

    return (
        <BaseForm id={formId} onFinish={handleSubmit} form={form} onValuesChange={onValuesChange}>
            <Card className="card-form" bordered={false}>
                <Row gutter={16}>
                    <Col span={12}>
                        <TextField
                            required
                            label={translate.formatMessage(commonMessage.restaurantName)}
                            name="restaurantName"
                        />
                    </Col>
                    <Col span={12}>
                        <TextField
                            required
                            label={translate.formatMessage(commonMessage.restaurantPath)}
                            name="restaurantPath"
                        />
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <CropImageField
                            label={translate.formatMessage(commonMessage.logoPath)}
                            name="logoPath"
                            imageUrl={imageUrl && `${AppConstants.contentRootUrl}${imageUrl}`}
                            aspect={1 / 1}
                            uploadFile={uploadFile}
                            handleRemoveImage={() => handleRemoveImage(setImageUrl, form)}
                        />
                    </Col>
                    <Col span={12}>
                        <CropImageField
                            label={translate.formatMessage(commonMessage.bannerPath)}
                            name="bannerPath"
                            imageUrl={bannerUrl && `${AppConstants.contentRootUrl}${bannerUrl}`}
                            aspect={40 / 9}
                            style={{ width: 170 }}
                            uploadFile={uploadBanner}
                            handleRemoveImage={() => handleRemoveImage(setBannerUrl, form)}
                        />
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <TextField
                            label={translate.formatMessage(commonMessage.hotline)}
                            name="hotline"
                        />
                    </Col>
                    <Col span={12}>
                        <TextField
                            required
                            label={translate.formatMessage(commonMessage.zipCode)}
                            name="zipCode"
                        />
                    </Col>
                    <Col span={12}>
                        <TextField
                            required
                            label={translate.formatMessage(commonMessage.city)}
                            name="city"
                        />
                    </Col>
                    <Col span={12}>
                        <TextField
                            required
                            label={translate.formatMessage(commonMessage.address)}
                            name="address"
                        />
                    </Col>
                    <Col span={12}>
                        <NumericField
                            formatter={(v) => v}
                            parser={(v) => v}
                            label={translate.formatMessage(commonMessage.latitude)}
                            name="latitude"
                        />
                    </Col>
                    <Col span={12}>
                        <NumericField
                            formatter={(v) => v}
                            parser={(v) => v}
                            label={translate.formatMessage(commonMessage.longitude)}
                            name="longitude"
                        />
                    </Col>
                    <Col span={12}>
                        <DatePickerField
                            label={<FormattedMessage defaultMessage="Expiration date" />}
                            name="expiredDate"
                            format={DATE_FORMAT_DISPLAY}
                            onChange={(value) => {
                                setExpiredDateDefault(value);
                            }}
                            rules={[
                                {
                                    required: true,
                                    message: translate.formatMessage(message.requiredExpiredDate),
                                },
                            ]}
                            style={{ width: '100%' }}
                        />
                    </Col>
                    <Col span={12}>
                        <DatePickerField
                            label={<FormattedMessage defaultMessage="Extend Date" />}
                            name="extendDate"
                            format={DATE_FORMAT_DISPLAY}
                            // disabledDate={}
                            showTime={{
                                defaultValue: dayjs(dayjs().add(1, 'hour').format('HH:00:00'), 'HH:mm:ss'),
                            }}
                            rules={[
                                {
                                    required: true,
                                    message: translate.formatMessage(message.requiredExtendDate),
                                },
                                {
                                    validator: validateDueDate,
                                },
                            ]}
                            style={{ width: '100%' }}
                        />
                    </Col>
                    
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <SelectField
                            name="services"
                            label={translate.formatMessage(commonMessage.services)}
                            options={restaurantServices}
                            mode="multiple"
                        />
                    </Col>
                    <Col span={12}>
                        <TextField
                            name="paypalClientId"
                            label={translate.formatMessage(commonMessage.paypalClientId)}
                        />
                    </Col>
                </Row>
                <Row gutter={16}>
                    <Col span={12}>
                        <TextField
                            name="paypalClientSecrect"
                            label={translate.formatMessage(commonMessage.paypalClientSecrect)}
                        />
                    </Col>
                    <Col span={12}>
                        <SelectField
                            name="paypalCurrentcyCode"
                            label={translate.formatMessage(commonMessage.paypalCurrentcyCode)}
                            options={paypalCurrentcyCodes}
                        />
                    </Col>
                    <Col span={12}>
                        <SelectField
                            required
                            name="lang"
                            label={translate.formatMessage(commonMessage.language)}
                            options={commonLanguages}
                        />
                    </Col>

                    <Col span={12}>
                        <SelectField
                            required
                            label={<FormattedMessage defaultMessage="Status" />}
                            name="status"
                            options={statusValues}
                        />
                    </Col>
                    <Col span={12}>
                        <Form.Item name="settings" hidden initialValue={1}>
                            <input type="hidden" />
                        </Form.Item>
                    </Col>
                </Row>              
                <div className="footer-card-form">{actions}</div>
            </Card>
        </BaseForm>
    );
};

export default RestaurantExpiredForm;